
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import {
  IObjectNotificationsProvider,
  IObjectNotificationsProviderMethods
} from "@/models/providers/objectNotifications";
import { CreateElement } from "vue";
import { objectNotificationsFilters } from "@/data/filters/objectNotifications";
import { objectNotificationsSorters } from "@/data/sorters/objectNotifications";
import { IObjectNotification } from "@/models/notifications";
import { UpmindObjectTypes } from "@/data/enums/objects";

@Component
export default class ObjectNotificationsProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ required: true })
  public objectType!: UpmindObjectTypes;

  @Prop({ required: true })
  public objectId!: string;

  @Prop({ default: "ONP1" })
  public declare id: string;

  // Data

  public isProcessing = false;

  public filters = objectNotificationsFilters;
  public sorters = objectNotificationsSorters;
  public dataModule = DataModules.OBJECT_NOTIFICATIONS;

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (this.loading) return h("is-loading", { props: { if: true } });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $notificationsData: this.providerData(),
        $notificationsMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.getData();
  }

  // Computed

  public get canCreate() {
    return this.$userCan("create_object_notification_mapping");
  }

  public get canDelete() {
    return this.$userCan("delete_object_notification_mapping");
  }

  // Methods

  public getStoreParams(params: any): Promise<any> {
    // Overrides mixin method
    return this.$_.merge(params, {
      objectType: this.objectType,
      objectId: this.objectId,
      params: {
        with: [
          "template",
          "template.category",
          "template.channel",
          "template.recipient_type,hook"
        ].join()
      }
    });
  }

  public reloadData() {
    return this.getData({ ignoreStored: true });
  }

  public openAddEditModal() {
    if (!this.canCreate) return;
    return this.$store.dispatch(
      `data/${DataModules.OBJECT_NOTIFICATIONS}/openAddEditModal`,
      {
        props: {
          objectType: this.objectType,
          objectId: this.objectId
        },
        events: {
          success: () => {
            this.reloadData();
          }
        }
      }
    );
  }

  public async deleteObjectNotification(id: string) {
    if (!this.canDelete) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t(
            "_sentence.object_notifications.delete_notification_confirmations"
          )
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(id);
            confirm.close();
          }
        }
      }
    });
  }

  public async onDeleteConfirmed(id): Promise<any> {
    try {
      await this.$store.dispatch(
        `data/${DataModules.OBJECT_NOTIFICATIONS}/remove`,
        {
          id
        }
      );
      this.reloadData();
      this.$buefy.toast.open({
        message: this.$t(
          "_sentence.object_notifications.notifation_mapping_deleted"
        ) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async openEditTemplateModal(id) {
    const modal = await this.$store.dispatch(
      `data/${DataModules.NOTIFICATIONS_TEMPLATES}/openAddTemplateModal`,
      {
        parent: this,
        props: {
          notificationTemplateId: id,
          showSubmitButton: false,
          showHookSelector: false,
          showChannelSelector: true,
          showActiveToggle: false,
          onlyEmitAfterSave: true
        },
        events: {
          "template-saved": async () => {
            modal.close();
            this.reloadData();
            this.$store.dispatch("toast/show", {
              message: this.$t("_sentence.successfully_saved")
            });
          }
        }
      }
    );
  }

  // Provide

  @Provide("objectNotificationsProvider")
  public providerData(): IObjectNotificationsProvider {
    return {
      activeFilters: this.activeFilters,
      enableFiltering: this.enableFiltering,
      enablePagination: this.enablePagination,
      enableSorting: this.enableSorting,
      filters: this.filters,
      isLoading: this.table.loading,
      limit: this.table.limit,
      objectNotifications: this.table.data as IObjectNotification[],
      page: this.table.page,
      sorters: this.sorters,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("objectNotificationsProviderMethods")
  public get providerMethods(): IObjectNotificationsProviderMethods {
    return {
      deleteObjectNotification: this.deleteObjectNotification,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openAddEditModal: this.openAddEditModal,
      openEditTemplateModal: this.openEditTemplateModal,
      reloadData: this.reloadData
    };
  }
}
